import { useParams } from 'react-router-dom';
import { Card, IconButton, Stack, Typography } from '@mui/material';
import {
  SVButton,
  SVFilePicker,
  SVForm,
  SVAutocomplete,
  SVTable,
} from '../core';
import {
  useAddBusinessDocument,
  useDeleteBusinessDocument,
  useGetBusinessDocuments,
} from '@/hooks/requests/useBusinesses';
import { useForm } from 'react-hook-form';
import {
  AddBusinessDocumentFields,
  BusinessDocumentType,
} from '@/types/businesses';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import { SVTableRow } from '@/types/common';
import { CloudDownload, Delete } from '@mui/icons-material';
import { useDownloadPresignedUrl } from '@/hooks/requests/usePresignedUrls';

export default function BusinessDocuments() {
  const { uuid = '' } = useParams();
  const { businessDocuments, isBusinessDocumentsPending, refetchDocuments } =
    useGetBusinessDocuments(uuid);
  const { addBusinessDocument } = useAddBusinessDocument();
  const { deleteBusinessDocument } = useDeleteBusinessDocument();
  const { downloadPresignedUrl } = useDownloadPresignedUrl();

  const formMethods = useForm<AddBusinessDocumentFields>({
    defaultValues: {
      url: '',
      documentDisplayName: '',
      businessId: uuid,
      businessDocumentType: '',
    },
    mode: 'onBlur',
  });

  const url = formMethods.watch('url');

  if (isBusinessDocumentsPending) {
    return null;
  }

  async function onDeleteBusinessDocument(id: string) {
    try {
      await deleteBusinessDocument(id);
      refetchDocuments();
      showToast({
        type: 'success',
        data: {
          content: 'The document has been succesfully deleted',
        },
      });
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  async function onDownloadDocument(uuid: string, file: string) {
    try {
      const url = new URL(file);
      const response = await downloadPresignedUrl({
        type: 'business document',
        uuid,
        file: url.pathname.slice(1),
      });
      window.open(response.url, '_blank');
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  const rows: SVTableRow[] = businessDocuments.map(document => {
    const { uuid, filename, url, businessDocumentType } = document;

    return {
      key: `documentRow${uuid}`,
      cells: [
        {
          content: filename,
          type: 'text',
          key: `filename${uuid}`,
        },
        {
          content: businessDocumentType,
          type: 'text',
          key: `type${uuid}`,
        },
        {
          content: (
            <IconButton onClick={() => onDownloadDocument(uuid, url)}>
              <CloudDownload />
            </IconButton>
          ),
          type: 'custom',
          key: `download${uuid}`,
        },
        {
          content: (
            <IconButton onClick={() => onDeleteBusinessDocument(uuid)}>
              <Delete />
            </IconButton>
          ),
          type: 'custom',
          key: `delete${uuid}`,
        },
      ],
    };
  });

  const businessDocumentTypeOptions = Object.entries(BusinessDocumentType).map(
    ([key, value]) => ({
      label: value,
      value: value,
      key: key,
    }),
  );

  async function onAddBusinessDocument(data: AddBusinessDocumentFields) {
    try {
      await addBusinessDocument(data);
      formMethods.reset();
      showToast({
        type: 'success',
        data: {
          content: 'The document has been succesfully added',
        },
      });
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  return (
    <>
      <Card>
        <Typography variant="h5" mb={2}>
          Documents
        </Typography>
        {businessDocuments.length === 0 ? (
          <Typography>No documents added</Typography>
        ) : (
          <SVTable
            ariaLabel="Documents"
            headers={[
              {
                content: 'Name',
                key: 'name',
              },
              {
                content: 'Type',
                key: 'type',
              },
              {
                content: 'Download',
                key: 'download',
              },
              {
                content: 'Delete',
                key: 'delete',
              },
            ]}
            rows={rows}
          />
        )}
      </Card>
      <Card>
        <Typography variant="h5" mb={2}>
          Upload Document
        </Typography>
        <SVForm methods={formMethods} onSubmit={onAddBusinessDocument}>
          <Stack spacing={2}>
            <SVAutocomplete
              name="businessDocumentType"
              label="Document Type"
              options={businessDocumentTypeOptions}
              rules={{ required: true }}
              disabled
            />
            <SVFilePicker
              name="url"
              documentDisplayName="documentDisplayName"
              label="Select Document"
              presignedUrlFileType="business document"
              uuid={uuid}
              acceptedFileTypes=".png,.jpg,.jpeg,.pdf"
              rules={{ required: true }}
            />
            <SVButton disabled={!url} type="submit">
              Upload
            </SVButton>
          </Stack>
        </SVForm>
      </Card>
    </>
  );
}
